import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { filtersFromSearchParams, applyFilter } from './laneUrlState';
import './laneshell-controls.css';

/**
 * Manual-lane "Task type" dropdown -- a view onto the `taskSource` URL param,
 * exactly as AssignedFilterDropdown is a view onto `assigned`. Values are the
 * backend's closed whitelist (task_lane_service._TASK_SOURCE_VALUES); the
 * rules that decide each type live there (MANUAL_KIND_SQL), not here.
 *
 * First built for the 70+ hold (John, 17 Aug: '70+' vs 'Standard'). Freshdesk
 * #682 (John, 22 Oct) asked for every manual task type as its own filter, with
 * staff-sent and automatic multi-rep follow-ups on ONE filter -- each row then
 * says which it is (MultiRepSentByLabel, ManualLane).
 *
 * 'ringfence' keeps its old value so a saved 70+ link still picks the right
 * option. 'standard' (the old "email_source is empty" split) still filters
 * exactly as before for a saved link, but is no longer offered: it is shown
 * under a plain name only while the URL carries it. Any other unknown URL
 * value is kept and synthesised as an option (never visually reset while the
 * URL still filters) -- same guard as AssignedFilterDropdown.
 */

const ARCHIVED_TOOLTIP = "Not applied -- archived rows don't record a task type (no email_source column).";

export const TASK_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All task types' },
  { value: 'chase', label: 'Chase client for info' },
  { value: 'multi_rep', label: 'Multi-rep' },
  { value: 'id_change', label: 'ID change reviews' },
  { value: 'staff', label: 'Staff-created tasks' },
  { value: 'ringfence', label: '70+ fee confirmations' },
];

/** Values a saved link may still carry that the backend still honours, but
 * which are no longer offered. Plain names, never the raw code. */
const RETIRED_LABELS: Record<string, string> = {
  standard: 'Standard manual task (old filter)',
};

export const TaskTypeFilterDropdown: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = filtersFromSearchParams(searchParams);

  const currentValue = filters.taskSource ?? '';
  const knownValues = new Set(TASK_TYPE_OPTIONS.map((o) => o.value));
  const hasUnknownValue = currentValue !== '' && !knownValues.has(currentValue);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSearchParams((prev) => applyFilter(prev, 'taskSource', value === '' ? null : value));
  };

  return (
    <div className="lane-shell__assigned-filter-wrap">
      <select
        className="lane-shell__assigned-filter"
        aria-label="Filter by task type"
        value={currentValue}
        onChange={handleChange}
        disabled={filters.archived}
        title={filters.archived ? ARCHIVED_TOOLTIP : undefined}
      >
        {TASK_TYPE_OPTIONS.map((o) => (
          <option key={o.value || 'all'} value={o.value}>{o.label}</option>
        ))}
        {hasUnknownValue && (
          <option value={currentValue}>{RETIRED_LABELS[currentValue] ?? currentValue}</option>
        )}
      </select>
    </div>
  );
};

export default TaskTypeFilterDropdown;
